import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Award, Globe, Users, TrendingUp, Zap, Shield } from "lucide-react";
import useScrollAnimation from "../hooks/useScrollAnimation";
import useCounterAnimation from "../hooks/useCounterAnimation";

const WhyChooseSection = () => {
  const [isVisible, sectionRef] = useScrollAnimation(0.2);
  const [yearsCount, yearsRef] = useCounterAnimation("45+", 2000, 200);
  const [countriesCount, countriesRef] = useCounterAnimation("170+", 2000, 400);
  const [clientsCount, clientsRef] = useCounterAnimation("150+", 2000, 600);

  const differentiators = [
    {
      icon: Award,
      title: "Industry Authority",
      description: "45+ years of trusted mining coverage and unparalleled industry expertise",
      highlight: "Market Leader",
      gradient: "from-yellow-500 to-amber-600"
    },
    {
      icon: Globe,
      title: "Global Reach",
      description: "170+ countries reached with localized content and regional expertise",
      highlight: "Worldwide Presence",
      gradient: "from-blue-500 to-cyan-600"
    },
    {
      icon: Users,
      title: "Decision Maker Access",
      description: "Direct connection to 68% of mining executives with purchase authority",
      highlight: "C-Suite Network",
      gradient: "from-purple-500 to-pink-600"
    },
    {
      icon: TrendingUp,
      title: "Proven ROI",
      description: "250% average ROI for clients with comprehensive campaign analytics",
      highlight: "Measurable Results",
      gradient: "from-green-500 to-emerald-600"
    }
  ];

  const achievements = [
    {
      number: yearsCount,
      label: "Years of Trust",
      ref: yearsRef,
      icon: Shield
    },
    {
      number: countriesCount,
      label: "Global Reach",
      ref: countriesRef,
      icon: Globe
    },
    {
      number: clientsCount,
      label: "Active Clients",
      ref: clientsRef,
      icon: Users
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className={`py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.1),transparent_50%)]"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20 mb-4">
            <Zap className="w-4 h-4 mr-2" />
            Why Industry Leaders Choose Us
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            <span className="text-yellow-500">Kitco Mining</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Four decades of mining expertise, global reach, and proven results make us the preferred partner 
            for mining companies worldwide.
          </p>
        </div>

        {/* Key Differentiators Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {differentiators.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <Card 
                key={index} 
                className="bg-gray-800/50 border-yellow-500/20 hover:border-yellow-500/40 transition-all duration-500 group hover:scale-105 backdrop-blur-sm"
                style={{
                  animation: isVisible ? `slideInUp 0.6s ease-out ${index * 0.1}s forwards` : 'none',
                  opacity: isVisible ? 1 : 0
                }}
              >
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg bg-gradient-to-br ${item.gradient} group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-xl font-bold text-white group-hover:text-yellow-500 transition-colors">
                          {item.title}
                        </h3>
                        <Badge variant="outline" className="border-yellow-500/40 text-yellow-500 text-xs">
                          {item.highlight}
                        </Badge>
                      </div>
                      <p className="text-gray-300 leading-relaxed group-hover:text-gray-200 transition-colors">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Achievement Numbers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {achievements.map((achievement, index) => {
            const IconComponent = achievement.icon;
            return (
              <div 
                key={index}
                ref={achievement.ref}
                className="text-center group cursor-pointer"
              >
                <div className="bg-gradient-to-br from-yellow-500/10 to-amber-600/10 rounded-full p-8 mb-4 group-hover:from-yellow-500/20 group-hover:to-amber-600/20 transition-all duration-300 inline-block">
                  <IconComponent className="w-8 h-8 text-yellow-500 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <div className="text-4xl md:text-5xl font-bold text-yellow-500 group-hover:scale-110 transition-transform duration-300">
                    {achievement.number}
                  </div>
                </div>
                <div className="text-white font-semibold text-lg">
                  {achievement.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default WhyChooseSection;